import { CommonModule } from '@angular/common';
import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { debounceTime, finalize, forkJoin, Subject } from 'rxjs';
import { MaterialModule } from '../material.module';
import { InstrumentFormDialogComponent } from './instrument-form-dialog.component';
import { InstrumentStatusDialogComponent, InstrumentStatusDialogMode } from './instrument-status-dialog.component';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import { LabEquipmentInstrument, LabEquipmentInstrumentStatus, LabEquipmentLocation } from './lab-equipment.models';
import { LabEquipmentService } from './lab-equipment.service';

const PAGE_SIZE_STORAGE_KEY = 'lab-equipment-page-size';
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

@Component({
  selector: 'app-lab-equipment',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
  templateUrl: './lab-equipment.component.html',
  styleUrl: './lab-equipment.component.scss',
})
export class LabEquipmentComponent implements OnInit {
  @ViewChild('equipmentPage') private equipmentPage?: ElementRef<HTMLElement>;
  @ViewChild('tableShell') private tableShell?: ElementRef<HTMLElement>;

  private readonly service = inject(LabEquipmentService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly textFilterChanges = new Subject<void>();
  readonly displayedColumns = [
    'equipmentNumber', 'name', 'manufacturer', 'modelNumber',
    'serialNumber', 'assetNumber', 'location', 'vendorDueStatus', 'labDueStatus', 'actions',
  ];
  readonly displayedFilterColumns = this.displayedColumns.map(column => `${column}Filter`);
  readonly sortMap: Record<string, string> = {
    equipmentNumber: 'equipmentNumber',
    name: 'name',
    manufacturer: 'manufacturer',
    modelNumber: 'modelNumber',
    serialNumber: 'serialNumber',
    assetNumber: 'assetNumber',
  };

  instruments: LabEquipmentInstrument[] = [];
  locations: LabEquipmentLocation[] = [];
  equipmentNumberFilter = '';
  nameFilter = '';
  manufacturerFilter = '';
  modelNumberFilter = '';
  serialNumberFilter = '';
  assetNumberFilter = '';
  locationIds: number[] = [];
  status: LabEquipmentInstrumentStatus = 'Active';
  vendorDueStatusFilter = '';
  labDueStatusFilter = '';
  pageIndex = 0;
  pageSize = 25;
  totalCount = 0;
  sort = 'name';
  descending = false;
  isLoading = true;
  isDeletingInstrument = false;
  errorMessage = '';
  private scrollToTableAfterLoad = false;

  ngOnInit(): void {
    this.restorePageSize();

    this.textFilterChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.applyFilters());

    forkJoin({ locations: this.service.getLocations() }).subscribe({
      next: ({ locations }) => {
        this.locations = locations;
        this.loadInstruments();
      },
      error: () => {
        this.isLoading = false;
        this.errorMessage = 'Locations could not be loaded. Please retry.';
      },
    });
  }

  applyFilters(): void {
    this.pageIndex = 0;
    this.scrollToTableAfterLoad = true;
    this.loadInstruments();
  }

  textFilterChanged(): void {
    this.textFilterChanges.next();
  }

  clearFilters(): void {
    this.equipmentNumberFilter = '';
    this.nameFilter = '';
    this.manufacturerFilter = '';
    this.modelNumberFilter = '';
    this.serialNumberFilter = '';
    this.assetNumberFilter = '';
    this.locationIds = [];
    this.vendorDueStatusFilter = '';
    this.labDueStatusFilter = '';
    this.applyFilters();
  }

  statusChanged(status: LabEquipmentInstrumentStatus): void {
    this.status = status;
    this.applyFilters();
  }

  get hasActiveFilters(): boolean {
    return Boolean(
      this.equipmentNumberFilter.trim()
      || this.nameFilter.trim()
      || this.manufacturerFilter.trim()
      || this.modelNumberFilter.trim()
      || this.serialNumberFilter.trim()
      || this.assetNumberFilter.trim()
      || this.locationIds.length
      || this.vendorDueStatusFilter
      || this.labDueStatusFilter,
    );
  }

  formatDueStatus(status: string | null | undefined): string {
    if (!status) return 'Not scheduled';
    const spaced = status.replace(/([a-z])([A-Z])/g, '$1 $2');
    return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
  }

  pageChanged(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.storePageSize();
    this.scrollToTableAfterLoad = true;
    this.loadInstruments();
  }

  private restorePageSize(): void {
    try {
      const storedPageSize = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
      if (PAGE_SIZE_OPTIONS.includes(storedPageSize)) this.pageSize = storedPageSize;
    } catch {
      // Storage may be unavailable due to browser privacy settings.
    }
  }

  private storePageSize(): void {
    try {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(this.pageSize));
    } catch {
      // Keep pagination functional when storage is unavailable.
    }
  }

  sortChanged(event: Sort): void {
    if (!event.direction) {
      this.sort = 'name';
      this.descending = false;
    } else {
      this.sort = this.sortMap[event.active] ?? 'name';
      this.descending = event.direction === 'desc';
    }
    this.pageIndex = 0;
    this.scrollToTableAfterLoad = true;
    this.loadInstruments();
  }

  openCreate(): void {
    this.openForm(null);
  }

  openTypes(): void {
    void this.router.navigate(['/lab-equipment/types']);
  }

  openEdit(instrument: LabEquipmentInstrument): void {
    if (!instrument.isDeleted) this.openForm(instrument);
  }

  openDetails(instrument: LabEquipmentInstrument): void {
    void this.router.navigate(['/lab-equipment', instrument.id]);
  }

  openStatusDialog(instrument: LabEquipmentInstrument, mode?: InstrumentStatusDialogMode): void {
    this.dialog.open(InstrumentStatusDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      data: { instrument, mode },
    }).afterClosed().subscribe(changed => {
      if (changed) this.loadInstruments();
    });
  }

  permanentlyDeleteInstrument(instrument: LabEquipmentInstrument): void {
    if (!instrument.isDeleted || this.isDeletingInstrument) return;
    this.dialog.open(ConfirmDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      data: {
        title: 'Permanently Delete Instrument',
        message: `This permanently deletes ${instrument.name} and its calibration, maintenance/repair, lifecycle history, and attached files from the application. This cannot be undone.`,
        confirmLabel: 'Delete Permanently',
        cancelLabel: 'Cancel',
        variant: 'warning',
      },
    }).afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.isDeletingInstrument = true;
      this.service.permanentlyDeleteInstrument(instrument.id)
        .pipe(finalize(() => this.isDeletingInstrument = false))
        .subscribe({
          next: () => this.loadInstruments(),
          error: () => this.errorMessage = 'The instrument could not be permanently deleted. Please retry.',
        });
    });
  }

  loadInstruments(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.service.getInstruments({
      equipmentNumber: this.equipmentNumberFilter.trim() || undefined,
      name: this.nameFilter.trim() || undefined,
      manufacturer: this.manufacturerFilter.trim() || undefined,
      modelNumber: this.modelNumberFilter.trim() || undefined,
      serialNumber: this.serialNumberFilter.trim() || undefined,
      assetNumber: this.assetNumberFilter.trim() || undefined,
      locationIds: this.locationIds,
      status: this.status,
      vendorDueStatus: this.vendorDueStatusFilter || undefined,
      labDueStatus: this.labDueStatusFilter || undefined,
      page: this.pageIndex + 1,
      pageSize: this.pageSize,
      sort: this.sort,
      descending: this.descending,
    }).pipe(finalize(() => this.isLoading = false)).subscribe({
      next: result => {
        this.instruments = result.items;
        this.totalCount = result.totalCount;
        this.scrollTableToTopIfRequested();
      },
      error: () => this.errorMessage = 'Instruments could not be loaded. Please retry.',
    });
  }

  private scrollTableToTopIfRequested(): void {
    if (!this.scrollToTableAfterLoad || !this.equipmentPage || !this.tableShell) return;
    this.equipmentPage.nativeElement.scrollTop = this.tableShell.nativeElement.offsetTop;
    this.scrollToTableAfterLoad = false;
  }

  private openForm(instrument: LabEquipmentInstrument | null): void {
    this.dialog.open(InstrumentFormDialogComponent, {
      width: '820px',
      maxWidth: '95vw',
      data: { instrument, locations: this.locations },
    }).afterClosed().subscribe(saved => {
      if (saved) this.loadInstruments();
    });
  }
}
